<script lang="ts">
    import {page} from "$app/stores";
    import EmployeeAvatar from "$lib/components/EmployeeAvatar.svelte";
    import type {Employee, EmployeeExtra} from "$lib/types/employee";
    import {inlineShortcut} from "$lib/utils/inlineShortcut";
    import EmploymentStatus from "$lib/components/EmploymentStatus.svelte";
    import TabButton from "$items/TabButton.svelte";
    import {writable, type Writable} from "svelte/store";
    import {setContext} from "svelte";
    import {browser} from "$app/environment";
    import {Button, Modal, Tooltip,} from "flowbite-svelte";
    import {
        BellSolid,
        BuildingSolid,
        CheckCircleSolid,
        ClipboardSolid,
        CloseCircleSolid,
        EditSolid,
        EnvelopeSolid,
        FolderSolid, GridOutline,
        InfoCircleOutline, ListOutline,
        MessagesSolid,
        PenOutline,
        TicketOutline,
        UserSolid
    } from "flowbite-svelte-icons";
    import NewContractModal from "$lib/partials/employee/NewContractModal.svelte";
    import EmployeeEmailModal from "$lib/partials/employee/EmployeeEmailModal.svelte";
    import EmployeeReminderModal from "$lib/partials/employee/EmployeeReminderModal.svelte";
    import StatusTriggerButtons from "$items/employee/StatusTriggerButtons.svelte";
    import GlobalShortcuts from "$lib/components/GlobalShortcuts.svelte";
    import {listView} from "$lib/stores/viewStore";
    import ReentryModal from "$lib/partials/employee/ReentryModal.svelte";
    import {asValue} from "$lib/utils/lenkradDate.js";
    import dayjs from "dayjs";

    let employee: Writable<Employee> = writable($page.data.employee);
    $: $employee = $page.data.employee;
    setContext('employee', employee);

    let originalEmployee: Employee;
    let reentryModal = false;

    let doneModal: Writable<boolean> = writable(false);
    setContext('doneModal', doneModal);

    const employee_extra: Writable<EmployeeExtra> = writable({
        tax_brackets: [],
        health_insurances: [],
        religious_denoms: [],
        iban_info: null,
        nationalities: [],
        graduations: [],
    });
    setContext('employee_extra', employee_extra);

    async function assign_options() {
        if ($employee_extra.tax_brackets.length === 0) {
            $employee_extra.tax_brackets = (await $page.data.lazy_tax_brackets)
        }
        if ($employee_extra.health_insurances.length === 0) {
            $employee_extra.health_insurances = (await $page.data.lazy_health_insurances)
        }
        if ($employee_extra.religious_denoms.length === 0) {
            $employee_extra.religious_denoms = (await $page.data.lazy_religious_denoms)
        }
        if ($employee_extra.nationalities.length === 0) {
            $employee_extra.nationalities = (await $page.data.lazy_nationalities)
        }
        if ($employee_extra.graduations.length === 0) {
            $employee_extra.graduations = (await $page.data.lazy_graduations)
        }

        $employee_extra.iban_info = (await $page.data.lazy_iban_info)
    }

    $:unit_name = $page.data.units.find(unit => unit.id === $employee.unitId)?.name?.split(' ')?.slice(1)?.join(' ')



    $: if (browser) {
        assign_options();
    }
    const super_edit = writable(false);
    setContext('super_edit', super_edit);

    let trigger_submits = writable(new Set<HTMLButtonElement | HTMLFormElement>());
    setContext('trigger_submits', trigger_submits);

    let reminderModal = false;
    let emailModal = false;
    let contractModal = false;

    function openEmailModal() {
        emailModal = true;
    }

    const createContract = () => {
        contractModal = true
    }

    function on_save() {
        $trigger_submits.forEach((button_or_form) => {
            if (button_or_form instanceof HTMLButtonElement) {
                button_or_form.click();
            } else if (button_or_form instanceof HTMLFormElement) {
                button_or_form.requestSubmit();
            }
        })
        $super_edit = false
    }



</script>

<GlobalShortcuts/>

<div class="flex flex-col md:flex-row items-start gap-3 px-4 pt-7">
    <div class="flex flex-1 flex-row items-center gap-3">
        <EmployeeAvatar editable={$super_edit} employee={$employee} size="2xl"/>
        <div class="flex flex-col items-start gap-1">
            <h1 class="text-base text-neutral-600 sm:text-lg md:text-2xl font-bold">{$employee.name}</h1>

            <span class="text-neutral-600">Unit: {unit_name}</span>
            <div class="text-neutral-600 text-[14px] flex gap-2"><TicketOutline class="text-primary-400 "/> <span>{$employee.employeeNumber || 'Bewerber'}</span></div>
        </div>
    </div>
    <div class="flex gap-3 mb-2">
        {#if !$super_edit}
        <Button
                on:click={() => reminderModal = true}
                class="bg-secondary-700 hover:bg-secondary-700"
                size="xs">
            <BellSolid
                    class="w-4 mr-2"/>
            <span>Reminder erstellen</span>
        </Button>
        <Button
                on:click={openEmailModal}
                class="bg-secondary-700 hover:bg-secondary-700"
                size="xs">
            <EnvelopeSolid
                    class="w-4 mr-2"/>
            <span> E-Mail verfassen </span>
        </Button>
        {/if}
        {#if $super_edit}
            <Button size="xs" class="bg-red-400" on:click={() => {super_edit.set(false); $employee = {...originalEmployee}}} >
                <CloseCircleSolid class="w-4 mr-2"/>
                Abbrechen
            </Button>
            <Button size="xs" on:click={on_save}>
                <CheckCircleSolid class="w-4 mr-2"/>
                Speichern
            </Button>
        {:else}
            {#if $page.url.pathname.match(/\/employee\/[a-f0-9-]+$/i) }
                <Button size="xs"  on:click={()=> {super_edit.set(true); originalEmployee = {...$employee}}} icon={EditSolid}>
                    <PenOutline class="w-4 mr-2"/>
                    <span>Bearbeiten</span>
                </Button>
            {/if}
            {#if (!$employee.currentContract || dayjs(asValue($employee.currentContract.completeTimeframe.endDate)).isAfter(dayjs().subtract(30, 'days'))) && ($employee.employmentStatus === 'active' || $employee.employmentStatus === 'training-verified' || $employee.employmentStatus === 'inactive' || $employee.employmentStatus === 'data-re-provided')}
                <Button on:click={() => $employee.contracts.length >0 ? reentryModal = true : createContract()} class="bg-sky-100 text-neutral-600" size="xs">
                    <BuildingSolid class="w-4 mr-2"/>
                    {#if $employee.contracts.length >0}
                        <span>Wiedereinstieg</span>
                    {:else}
                        <span>Vertragsangebot erstellen</span>
                    {/if}

                </Button>
            {:else if !$employee.currentContract || $employee.employmentStatus === 'rebuddy'}
                <StatusTriggerButtons bind:employee={$employee}/>
            {/if}
        {/if}

    </div>

</div>


<div class="flex flex-row items-end justify-between w-full md:border-b border-gray-300 mt-5">
    <div class="flex flex-wrap md:flex-nowrap flex-row justify-start items-center gap-3 md:gap-0">
        <TabButton href="/employee/{$page.params.uuid}" icon={UserSolid}
                   title="Stammdaten"/>
        <TabButton href="/employee/{$page.params.uuid}/vertrag" icon={ClipboardSolid}
                   title="Vertrag / Gehalt"/>
        <TabButton href="/employee/{$page.params.uuid}/dokumente" icon={FolderSolid}
                   title="Dokumente"/>
        {#if $employee.userId}
        <TabButton href="/employee/{$page.params.uuid}/connect" icon={MessagesSolid}
                   title="Connect"/>
        {/if}
    </div>

    <div class="max-w-sm w-full pl-[1.1rem] flex justify-between">
        <div class="h-[50px] flex gap-1 items-center">
            <div class="text-neutral-600 dark:text-gray-400 whitespace-nowrap relative"><InfoCircleOutline class="text-primary-400"/></div>
            <div ><EmploymentStatus editable={$super_edit} size="sm" bind:status={$employee.employmentStatus} uuid={$employee.uuid}/></div>
        </div>
        <div class="pr-3 pt-1">
            <Button on:click={() => $listView = !$listView} color="alternative" pill class="!p-2" >
            {#if $listView}
                <ListOutline class="text-primary-600" />
            {:else}
                <GridOutline class="text-primary-600" />
            {/if}
            </Button>
            <Tooltip>Ansicht wechseln</Tooltip>
        </div>

    </div>
</div>
{#if $employee.contracts.length >0}
<ReentryModal bind:open={reentryModal} contract={$employee.currentContract} employee={$employee} on:close={() => reentryModal = false}/>
{/if}
<EmployeeEmailModal bind:open={emailModal} bind:employee={$employee}/>
<NewContractModal bind:open={contractModal} bind:employee={$employee}/>

<EmployeeReminderModal bind:open={reminderModal} bind:employee={$employee}/>


<div class="pt-12 px-0">
    <slot/>
</div>
<Modal bind:open={$doneModal} size="xs" autoclose>
    <div class="text-center">
        <CheckCircleSolid class="mx-auto mb-4 text-green-500 w-12 h-12 dark:text-gray-200" />
        <h3 class="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">Vertragsdokument erstellt & versendet!</h3>
        <Button on:click={() => window.location.reload()}>OK</Button>
    </div>
</Modal>
